import { FRAG_URLS } from "./pathmap.mjs";

// ---------- helpers ----------
const ensureHtml = (s) => (/\.html?$/i.test(s) ? s : `${s}.html`);
const strip = (s) => String(s).replace(/^\/+|\/+$/g, "");
const join = (...parts) => "/" + parts.filter(Boolean).map(strip).join("/");
const toAbs = (u) => (/^[a-z]+:/i.test(u) ? u : `${location.origin}${u.startsWith("/") ? u : "/" + u}`);

function resolveToken(tok = "") {
	tok = tok.trim();
	if (tok.startsWith("app:")) return join("/app/pages", ensureHtml(tok.slice(4).trim()));
	if (tok.startsWith("site:")) return join(ensureHtml(tok.slice(5).trim()));
	return tok;
}

function wireLinks(scope = document) {
	scope.querySelectorAll("a[data-href]").forEach((a) => {
		const path = resolveToken(a.getAttribute("data-href") || "");
		a.setAttribute("href", toAbs(path));
		a.removeAttribute("data-href");
	});
}

function sanitize(html) {
	try {
		const doc = new DOMParser().parseFromString(String(html), "text/html");
		doc.querySelectorAll('script, link[rel="stylesheet"], link[rel="modulepreload"]').forEach((n) => n.remove());
		return doc.body.innerHTML || String(html);
	} catch {
		return String(html);
	}
}

// ---------- core loader (FRAG_URLS-first) ----------
const fetchCache = new Map();

async function loadFragment(rawKey) {
	const key = String(rawKey || "").trim();
	if (!key) return "";

	// Prefer a Parcel-managed URL (no SPA fallbacks, always served)
	const parcelUrl = FRAG_URLS?.[key];
	if (parcelUrl) {
		if (!fetchCache.has(parcelUrl)) {
			fetchCache.set(
				parcelUrl,
				fetch(parcelUrl).then((r) => (r.ok ? r.text() : ""))
			);
		}
		const viaParcel = await fetchCache.get(parcelUrl);
		if (viaParcel && viaParcel.trim()) return viaParcel;
	}

	// Final fallback: fetch by resolved path (useful on simple static servers)
	const resolved = resolveToken(key);
	const path = ensureHtml(resolved.startsWith("/") ? resolved : "/" + resolved);
	const abs = toAbs(path);
	if (!fetchCache.has(abs)) {
		fetchCache.set(
			abs,
			fetch(abs).then((r) => (r.ok ? r.text() : ""))
		);
	}
	const fetched = await fetchCache.get(abs);
	if (fetched && fetched.trim()) return fetched;

	console.warn("[loader] include miss", {
		raw: key,
		triedParcel: parcelUrl || null,
		triedPath: abs,
		keys: Object.keys(FRAG_URLS || {}),
	});
	return "";
}

export async function injectIncludes() {
	const nodes = [...document.querySelectorAll("[data-include]:not([data-included])")];

	await Promise.all(
		nodes.map(async (el) => {
			const raw = el.getAttribute("data-include") || "";
			const html = await loadFragment(raw);

			const wrapper = document.createElement("div");
			wrapper.innerHTML = html
				? sanitize(html)
				: `<div class="text-sm text-red-700 bg-red-50 border border-red-200 p-2 rounded">Missing include: ${raw}</div>`;

			// ✅ Instead of replacing the <div> itself, replace only its children
			while (el.firstChild) el.removeChild(el.firstChild);
			for (const child of [...wrapper.children]) el.appendChild(child);

			el.setAttribute("data-included", "1");
			wireLinks(el);
		})
	);

	wireLinks(document);

	// ✅ Notify that all includes are loaded (optional global hook)
	document.dispatchEvent(new Event("includes:ready"));
	return true;
}

// ---------- expose + boot ----------
window._irecruit = { FRAG_URLS, injectIncludes, wireLinks };

if (!window.__irecruitLoaderBooted) {
	window.__irecruitLoaderBooted = true;

	async function startIncludes() {
		await injectIncludes();
		// 🔹 Dispatch global ready event
		document.dispatchEvent(new Event("includes:ready"));
		console.log("[loader] includes:ready fired");
	}

	if (document.readyState === "loading") {
		document.addEventListener("DOMContentLoaded", startIncludes, { once: true });
	} else {
		startIncludes();
	}
}
